import { useContext } from 'react';
import { Flex } from '@chakra-ui/react';
import { Button, TooltipProvider } from '@kula-ai/components';
import { ClockRewind } from '@kula-ai/icons-core';
import NavigationContext from '@/layout/side-nav/context';
import {
  BAR_BUTTON_HEIGHT_PX,
  BAR_CONTROL_GAP_PX,
  BAR_HEIGHT_PX,
  BAR_OFFSET_RIGHT_PX,
} from '../../constants';
import { useBarRouteVisibility, useReserveBottomBarSpace } from '../../hooks';
import { useAgentForgeStore } from '../../stores';
import { SessionTab } from './SessionTab';

// Mirrors the widths in src/layout/side-nav/index.tsx.
const SIDE_NAV_WIDTH_COLLAPSED_PX = 58;
const SIDE_NAV_WIDTH_EXPANDED_PX = 240;

const HISTORY_BASE_SIZE_CLASS =
  `!h-[${BAR_BUTTON_HEIGHT_PX}px] !min-h-[${BAR_BUTTON_HEIGHT_PX}px] ` +
  `!w-[${BAR_BUTTON_HEIGHT_PX}px] !min-w-[${BAR_BUTTON_HEIGHT_PX}px] ` +
  `!p-0 !rounded-md`;

const HISTORY_INACTIVE_CLASSNAME = `bg-base-white text-secondary-800 border border-secondary-100 hover:bg-base-white ${HISTORY_BASE_SIZE_CLASS}`;
const HISTORY_ACTIVE_CLASSNAME =
  `bg-secondary-800 text-base-white hover:bg-secondary-800 ` +
  `active:!bg-secondary-800 ${HISTORY_BASE_SIZE_CLASS}`;

// Renders when mode is 'floating' or 'minimized' (i.e. the user navigated to a
// result page from AgentForge). Shows the session tab + history toggle alongside
// the floating overlay, and stays visible after the user minimizes. No "new
// chat" button (CORE-4669).
export const AgentForgeBar = () => {
  const visible = useBarRouteVisibility();
  const mode = useAgentForgeStore((s) => s.mode);
  const isActive = mode === 'floating' || mode === 'minimized';
  // Reserve <main> bottom padding while the bar is visible.
  useReserveBottomBarSpace(visible && isActive);

  const navContext = useContext(NavigationContext) as
    | { isNavCollapsed?: boolean }
    | undefined;
  const sideNavWidthPx = navContext?.isNavCollapsed
    ? SIDE_NAV_WIDTH_COLLAPSED_PX
    : SIDE_NAV_WIDTH_EXPANDED_PX;

  const openTabs = useAgentForgeStore((s) => s.openTabs);
  const activeSessionId = useAgentForgeStore((s) => s.activeSessionId);
  const historyOpen = useAgentForgeStore((s) => s.historyOpen);
  const closeTab = useAgentForgeStore((s) => s.closeTab);
  const setActive = useAgentForgeStore((s) => s.setActive);
  const setMode = useAgentForgeStore((s) => s.setMode);
  const toggleHistory = useAgentForgeStore((s) => s.toggleHistory);

  if (!visible || !isActive) return null;

  const handleSelectTab = (sessionId: string) => {
    setActive(sessionId);
    setMode('floating');
  };

  const handleCloseTab = (sessionId: string) => {
    closeTab(sessionId);
    setMode('docked');
  };

  return (
    <TooltipProvider>
      <Flex
        data-test-id="agentforge-bar"
        role="region"
        aria-label="Agent Stack"
        pos="fixed"
        bottom="0"
        left={`${sideNavWidthPx}px`}
        right="0"
        transition="left 0.25s"
        h={`${BAR_HEIGHT_PX}px`}
        zIndex="overlay"
        align="center"
        justify="flex-end"
        gap={`${BAR_CONTROL_GAP_PX}px`}
        pr={`${BAR_OFFSET_RIGHT_PX}px`}
        borderTop="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        {openTabs
          .filter((tab) => tab.sessionId === activeSessionId)
          .map((tab) => (
            <SessionTab
              key={tab.sessionId}
              tab={tab}
              isActive
              onSelect={handleSelectTab}
              onClose={handleCloseTab}
            />
          ))}
        <Button
          size="icon"
          aria-label="Open history"
          height="26px"
          width="26px"
          aria-pressed={historyOpen}
          className={
            historyOpen ? HISTORY_ACTIVE_CLASSNAME : HISTORY_INACTIVE_CLASSNAME
          }
          onClick={() => toggleHistory()}
        >
          <ClockRewind size={16} color="currentColor" />
        </Button>
      </Flex>
    </TooltipProvider>
  );
};
